import type { MetricResult } from '../../types';
import { attachMockRegistryToMetrics } from '../mockRegistryMeta';

const rawMetricResultsMock: Record<string, MetricResult> = {
  sales_influenced: {
    display_name: 'Sales influenced',
    meta: {
      query:
        'SELECT /* mock */ * FROM sales_influenced WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 36,
    format: 'count',
    description: '13% of your total April sales',
    trend: { direction: 'up', label: 'up from last month', sentiment: 'good' },
  },
  cold_leads_revived: {
    display_name: 'Revived this month',
    meta: {
      query:
        'SELECT /* mock */ * FROM cold_leads_revived WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 152,
    format: 'count',
    trend: { direction: 'up', label: 'up from last month', sentiment: 'good' },
  },
  conversion_rate: {
    display_name: "Conversion on Revived",
    meta: {
      query: "SELECT /* mock */ * FROM conversion_rate WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 24,
    format: 'percent',
    description: 'vs 5% benchmark — 4.8x best-in-class',
  },
  oldest_lead_days_to_revival: {
    display_name: "Oldest Lead Revived",
    meta: {
      query: "SELECT /* mock */ * FROM oldest_lead_days_to_revival WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 708,
    format: 'days',
    description: 'silent before re-engaging',
  },
  revived_sales_by_vehicle_model: {
    display_name: 'Top models from revived sales',
    overline: 'What they bought',
    meta: {
      query:
        'SELECT /* mock */ * FROM revived_sales_by_vehicle_model WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 36,
    description: '16 unique models sold',
    breakdown: [
      { label: 'Super Duty', value: 9, pct: 25 },
      { label: 'F-150', value: 7, pct: 19 },
      { label: 'Bronco', value: 5, pct: 14 },
      { label: '15 other models', value: 15, pct: 42, color: 'grey' },
    ],
    footerLink: { label: 'See full breakdown', href: '#' },
  },
  revival_prior_crm_status_share: {
    display_name: 'CRM status when we enrolled them',
    overline: 'Where they came from',
    meta: {
      query: "SELECT /* mock */ * FROM revival_prior_crm_status_share WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 33,
    format: 'percent',
    breakdown: [
      { label: 'Cold / unqualified', value: 67, pct: 67, color: 'info' },
      { label: 'Lost / inactive', value: 22, pct: 22, color: 'error' },
      { label: 'Bad / unworkable', value: 11, pct: 11, color: 'warning' },
    ],
    footerLink: { label: 'See cold lead pipeline', href: '#' },
  },
  revived_sales_by_content_theme: {
    display_name: 'Top converting messages',
    overline: 'What convinced them',
    meta: {
      query: "SELECT /* mock */ * FROM revived_sales_by_content_theme WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 3,
    breakdown: [
      { label: 'Priced-to-move inventory', value: 11 },
      { label: 'New vehicle savings vs. MSRP', value: 9 },
      { label: 'Lease specials', value: 6 },
    ],
    footerLink: { label: 'See all activity', href: '#' },
  },
  lead_capture_missing_email_rate: {
    display_name: "Lead capture without email",
    meta: {
      query: "SELECT /* mock */ * FROM lead_capture_missing_email_rate WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 34.9,
    format: 'percent',
    trend: {
      direction: 'up',
      label: 'up 2pp from last month (worse)',
      sentiment: 'bad',
    },
    benchmark: {
      best: 5,
      baseline: 40,
      current: 34.9,
      rangeLabels: ['Baseline 40%', 'Best 5%'],
    },
  },
  lead_capture_missing_voi_rate: {
    display_name: 'Lead capture without VOI',
    meta: {
      query:
        'SELECT /* mock */ * FROM lead_capture_missing_voi_rate WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 14.7,
    format: 'percent',
    trend: {
      direction: 'down',
      label: 'down 3pp from last month (better)',
      sentiment: 'good',
    },
    benchmark: {
      best: 5,
      baseline: 30,
      current: 14.7,
      rangeLabels: ['Baseline 30%', 'Best 5%'],
    },
  },
  internet_conversion_rate: {
    display_name: "Internet conversion",
    meta: {
      query: "SELECT /* mock */ * FROM internet_conversion_rate WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 4.1,
    format: 'percent',
    trend: {
      direction: 'down',
      label: 'down 2pp from last month (worse)',
      sentiment: 'bad',
    },
    benchmark: {
      best: 10,
      baseline: 15,
      current: 4.1,
      rangeLabels: ['Baseline 15%', 'Best 10%'],
    },
  },
  attributed_internet_conversion_rate: {
    display_name: "Internet conversion",
    meta: {
      query: "SELECT /* mock */ * FROM attributed_internet_conversion_rate WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 4.1,
    format: 'percent',
    trend: {
      direction: 'down',
      label: 'down 2pp from last month (worse)',
      sentiment: 'bad',
    },
    benchmark: {
      best: 10,
      baseline: 15,
      current: 4.1,
      rangeLabels: ['Baseline 15%', 'Best 10%'],
    },
  },
  revived_sales_from_leads_older_than_90d_share: {
    display_name: "Revived from leads older than 90 days",
    meta: {
      query: "SELECT /* mock */ * FROM revived_sales_from_leads_older_than_90d_share WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 97,
    format: 'percent',
    description: 'of revived sales came from leads older than 90 days at original inquiry.',
  },
  revived_sales_marked_dead_in_crm_share: {
    display_name: "Marked dead in CRM at pickup",
    meta: {
      query: "SELECT /* mock */ * FROM revived_sales_marked_dead_in_crm_share WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 33,
    format: 'percent',
    description: 'were marked LOST, INACTIVE or BAD in your CRM when we picked them up.',
    breakdown: [
      { label: 'Cold / unqualified', value: 67, pct: 67 },
      { label: 'Lost / inactive', value: 22, pct: 22 },
      { label: 'Bad / unworkable', value: 11, pct: 11 },
    ],
  },
  revived_sales_no_prior_relationship_share: {
    display_name: "No prior buying relationship",
    meta: {
      query: "SELECT /* mock */ * FROM revived_sales_no_prior_relationship_share WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 86,
    format: 'percent',
    description: 'came from sources where you had no prior buying relationship.',
    breakdown: [
      { label: 'Internet', value: 42, pct: 42 },
      { label: 'Third-party', value: 28, pct: 28 },
      { label: 'Walk-in', value: 16, pct: 16 },
      { label: 'Repeat/referral', value: 14, pct: 14 },
    ],
  },
  unsold_leads_count: {
    display_name: "Leads went unsold",
    meta: {
      query: "SELECT /* mock */ * FROM unsold_leads_count WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 2340,
    format: 'count',
    description:
      "These are operational metrics your team owns. We surface them because they shape what nurturing can do for you — and where you're already strong.",
    badge: {
      label: "88 of those went uncontactable — email wasn't captured at intake",
      tone: 'danger',
    },
    trend: {
      direction: 'flat',
      label: '2,616 total leads · 276 sold',
      sentiment: 'neutral',
    },
  },
  lead_capture_missing_email_rate_phone: {
    display_name: "Lead capture without email \u00b7 Phone",
    meta: {
      query: "SELECT /* mock */ * FROM lead_capture_missing_email_rate_phone WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 34.9,
    format: 'percent',
    trend: { direction: 'down', label: '5pp from baseline', sentiment: 'bad' },
    benchmark: {
      best: 5,
      baseline: 40,
      current: 34.9,
      rangeLabels: ['Baseline 40%', 'Best 5%'],
    },
  },
  lead_capture_missing_email_rate_walk_in: {
    display_name: "Lead capture without email \u00b7 Walk-in",
    meta: {
      query: "SELECT /* mock */ * FROM lead_capture_missing_email_rate_walk_in WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 20.8,
    format: 'percent',
    trend: { direction: 'down', label: '29pp from baseline', sentiment: 'bad' },
    benchmark: {
      best: 5,
      baseline: 50,
      current: 20.8,
      rangeLabels: ['Baseline 50%', 'Best 5%'],
    },
  },
  unsubscribe_rate: {
    display_name: 'Unsubscribe rate',
    meta: {
      query:
        'SELECT /* mock */ * FROM unsubscribe_rate WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 0.67,
    format: 'percent',
    description: 'well below 2% threshold',
    benchmark: {
      best: 5,
      baseline: 30,
      current: 0.67,
      rangeLabels: ['Baseline 30%', 'Best 5%'],
    },
  },
  walk_in_conversion_rate: {
    display_name: "Walk-in conversion",
    meta: {
      query: "SELECT /* mock */ * FROM walk_in_conversion_rate WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 73.5,
    format: 'percent',
    trend: { direction: 'up', label: 'Above best-in-class', sentiment: 'good' },
    benchmark: {
      best: 40,
      baseline: 30,
      current: 73.5,
      rangeLabels: ['Baseline 30%', 'Best 40%'],
    },
  },
  phone_up_conversion_rate: {
    display_name: "Phone-up conversion",
    meta: {
      query: "SELECT /* mock */ * FROM phone_up_conversion_rate WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 0.2,
    format: 'percent',
    trend: { direction: 'down', label: 'Significant gap', sentiment: 'bad' },
    benchmark: {
      best: 15,
      baseline: 10,
      current: 0.2,
      rangeLabels: ['Baseline 10%', 'Best 15%'],
    },
  },
  salesperson_email_capture_rate: {
    display_name: "Where the front-line gaps are",
    meta: {
      query: "SELECT /* mock */ * FROM salesperson_email_capture_rate WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 4,
    table: {
      columns: [
        { key: 'salesperson', label: 'SALESPERSON', type: 'text' },
        { key: 'emailCapture', label: 'EMAIL CAPTURE', type: 'trend' },
        { key: 'voiCapture', label: 'VOI CAPTURE', type: 'trend' },
        { key: 'percentMarkedDead', label: '% MARKED DEAD', type: 'text' },
        { key: 'flagLabel', label: 'FLAG', type: 'badge' },
      ],
      rows: [
        {
          salesperson: 'Marco D.',
          emailCapture: '94%',
          emailCaptureTrend: 'up',
          voiCapture: '88%',
          voiCaptureTrend: 'up',
          percentMarkedDead: '8%',
          flagLabel: 'Strong',
          flagTone: 'success',
        },
        {
          salesperson: 'Jenna R.',
          emailCapture: '71%',
          voiCapture: '62%',
          percentMarkedDead: '14%',
          flagLabel: 'Mid-pack',
          flagTone: 'info',
        },
        {
          salesperson: 'Devon K.',
          emailCapture: '42%',
          voiCapture: '31%',
          percentMarkedDead: '38%',
          highlightMarkedDead: true,
          flagLabel: 'Coach',
          flagTone: 'warning',
        },
        {
          salesperson: 'Priya M.',
          emailCapture: '83%',
          voiCapture: '79%',
          percentMarkedDead: '11%',
          flagLabel: 'Strong',
          flagTone: 'success',
        },
      ],
    },
  },
  top_n_nameplate_share: {
    display_name: "Top 3 Nameplate Share",
    meta: {
      query: "SELECT /* mock */ * FROM top_n_nameplate_share WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 58,
    format: 'percent',
    trend: { direction: 'down', label: '10%', sentiment: 'bad' },
  },
  revived_sales_new_units: {
    display_name: "New",
    meta: {
      query: "SELECT /* mock */ * FROM revived_sales_new_units WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 23,
    format: 'count',
    trend: { direction: 'down', label: '2', sentiment: 'good' },
  },
  revived_sales_used_cpo_units: {
    display_name: "Used/CPO",
    meta: {
      query: "SELECT /* mock */ * FROM revived_sales_used_cpo_units WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 13,
    format: 'count',
    trend: { direction: 'up', label: '3', sentiment: 'good' },
  },
  personalized_touchpoints: {
    display_name: "Personalized touchpoints delivered",
    meta: {
      query: "SELECT /* mock */ * FROM personalized_touchpoints WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 1901,
    format: 'count',
    description: 'Personalized touchpoints delivered',
  },
  buyer_profile_count: {
    display_name: 'Buyer Profiles',
    meta: {
      query:
        'SELECT /* mock */ * FROM buyer_profile_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 8,
    format: 'count',
    trend: { direction: 'flat', label: 'Tuned - Steady', sentiment: 'good' },
    benchmark: {
      best: 12,
      baseline: 4,
      current: 8,
      rangeLabels: ['Baseline 4', 'Best 12'],
    },
  },
  engagement_rate: {
    display_name: 'Engagement rate',
    meta: {
      query:
        'SELECT /* mock */ * FROM engagement_rate WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 29.4,
    format: 'percent',
    description: 'vs ~2% industry avg',
    // No trend — Pipeline statCard shows description; Activity gauge still has bar + baseline/best.
    benchmark: {
      best: 40,
      baseline: 20,
      current: 29.4,
    },
  },
  content_theme_sent_count: {
    display_name: 'What moved customers during this Time Period',
    meta: {
      query: "SELECT /* mock */ * FROM content_theme_sent_count WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 6,
    table: {
      columns: [
        { key: 'label', label: 'CONTENT THEME', type: 'text' },
        { key: 'sent', label: 'SENT', type: 'number' },
        { key: 'engaged', label: 'ENGAGED', type: 'number' },
        { key: 'converted', label: 'CONVERTED', type: 'bar' },
      ],
      rows: [
        { label: "Dealer's priced-to-move inventory", sent: 412, engaged: 118, converted: 11, share: 100 },
        { label: 'New vehicle savings & finance offers', sent: 386, engaged: 102, converted: 9, share: 82 },
        { label: 'Lease specials', sent: 298, engaged: 76, converted: 7, share: 64 },
        { label: 'Trade-in value updates', sent: 245, engaged: 58, converted: 6, share: 55 },
        { label: 'Model comparison guides', sent: 198, engaged: 49, converted: 5, share: 45 },
        { label: 'Service & maintenance reminders', sent: 162, engaged: 41, converted: 4, share: 36 },
      ],
    },
  },
  customers_sit_right_now: {
    display_name: 'Where 17,298 customers sit right now',
    meta: {
      query:
        'SELECT /* mock */ * FROM customers_sit_right_now WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: '',
  },
  cohort_engaged_count: {
    display_name: 'Engaged',
    meta: {
      query:
        'SELECT /* mock */ * FROM cohort_engaged_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 4142,
    format: 'count',
    breakdown: [{ label: 'Engaged', value: 4142, pct: 24, color: 'primary' }],
  },
  cohort_recent_additions_count: {
    display_name: 'Period additions',
    meta: {
      query:
        'SELECT /* mock */ * FROM cohort_recent_additions_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 2876,
    format: 'count',
    breakdown: [{ label: 'Period additions', value: 2876, pct: 17, color: 'grey' }],
  },
  cohort_period_removals_count: {
    display_name: 'Period removals',
    meta: {
      query:
        'SELECT /* mock */ * FROM cohort_period_removals_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 842,
    format: 'count',
    breakdown: [{ label: 'Period removals', value: 842, pct: 5, color: 'grey' }],
  },
  cohort_long_term_nurturing_count: {
    display_name: 'Long Term Nurturing',
    meta: {
      query:
        'SELECT /* mock */ * FROM cohort_long_term_nurturing_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 9164,
    format: 'count',
    breakdown: [{ label: 'Long Term Nurturing', value: 9164, pct: 53, color: 'grey' }],
  },
  cohort_unsubscribed_count: {
    display_name: "Unsubscribed",
    meta: {
      query: "SELECT /* mock */ * FROM cohort_unsubscribed_count WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 1116,
    format: 'count',
    breakdown: [{ label: 'Unsubscribed', value: 1116, pct: 6, color: 'warning' }],
  },
  prospects_under_nurturing: {
    display_name: "Pipeline",
    meta: {
      query: "SELECT /* mock */ * FROM prospects_under_nurturing WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 17298,
    format: 'count',
    description: 'Cold leads being nurtured back to your showroom',
    trend: {
      direction: 'up',
      label: '412 new cold leads enrolled this month',
      sentiment: 'good',
    },
    badge: {
      label: 'customers across 16 buyer segments',
      tone: 'info',
    },
  },
  new_enrollments_by_vehicle_model: {
    display_name: "Where the new cold leads landed",
    meta: {
      query: "SELECT /* mock */ * FROM new_enrollments_by_vehicle_model WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 412,
    breakdown: [
      { label: 'F-150', value: 124 },
      { label: 'Super Duty', value: 87 },
      { label: 'Explorer', value: 64 },
      { label: 'Bronco', value: 45 },
      { label: 'Escape', value: 38 },
      { label: 'Maverick', value: 22 },
    ],
  },
  buyer_segment_count: {
    display_name: "How each audience is performing",
    meta: {
      query: "SELECT /* mock */ * FROM buyer_segment_count WHERE dealer_id = :dealer_id AND period = :time_period",
    },
    value: 6,
    table: {
      columns: [
        { key: 'label', label: 'SEGMENT', type: 'text' },
        { key: 'inNurturing', label: 'IN NURTURING', type: 'number' },
        { key: 'newThisMonth', label: 'NEW THIS MONTH', type: 'number' },
        { key: 'revived', label: 'REVIVED', type: 'number' },
        { key: 'sold', label: 'SOLD', type: 'number' },
        { key: 'conversion', label: 'CONVERSION', type: 'bar' },
      ],
      rows: [
        {
          label: 'Super Duty',
          inNurturing: 2890,
          newThisMonth: 87,
          newChangeLabel: '+87',
          revived: 18,
          sold: 9,
          conversion: '67%',
          share: 67,
        },
        {
          label: 'F-150',
          inNurturing: 4412,
          newThisMonth: 124,
          newChangeLabel: '+124',
          revived: 24,
          sold: 12,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Bronco',
          inNurturing: 1876,
          newThisMonth: 45,
          newChangeLabel: '+45',
          revived: 11,
          sold: 5,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Explorer',
          inNurturing: 2104,
          newThisMonth: 64,
          newChangeLabel: '+64',
          revived: 8,
          sold: 4,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Mustang',
          inNurturing: 982,
          newThisMonth: 28,
          newChangeLabel: '+28',
          revived: 4,
          sold: 2,
          conversion: '100%',
          share: 100,
        },
        {
          label: 'Expedition',
          inNurturing: 756,
          newThisMonth: 19,
          newChangeLabel: '+19',
          revived: 3,
          sold: 0,
          conversion: '—',
        },
      ],
    },
  },
  focus_model_nurturing_pool_count: {
    display_name: 'F-150',
    meta: {
      query:
        'SELECT /* mock */ * FROM focus_model_nurturing_pool_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 4412,
    format: 'count',
    description: 'Lease & finance offers · hybrid framing',
    badge: { label: 'Top performer', tone: 'success' },
  },
  focus_models_nurturing_pool_share: {
    display_name: 'Super Duty',
    meta: {
      query:
        'SELECT /* mock */ * FROM focus_models_nurturing_pool_share WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 972,
    format: 'count',
    description: 'Payment calculator · trade equity framing',
    badge: { label: 'Slow mover', tone: 'warning' },
  },
  model_performance_status: {
    display_name: 'Bronco',
    meta: {
      query:
        'SELECT /* mock */ * FROM model_performance_status WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 2312,
    format: 'count',
    description: 'Adventure lifestyle · accessory offers',
    badge: { label: 'Strong', tone: 'info' },
  },
  inventory_aged_unit_count: {
    display_name: 'Bronco Sport',
    meta: {
      query:
        'SELECT /* mock */ * FROM inventory_aged_unit_count WHERE dealer_id = :dealer_id AND period = :time_period',
    },
    value: 1412,
    format: 'count',
    description: 'First-time buyer · payment comfort',
    badge: { label: 'Growth', tone: 'neutral' },
  },
};

export const metricResultsMock = attachMockRegistryToMetrics(rawMetricResultsMock);
